import React from 'react';
import { Award, Shield, Users, Zap, CheckCircle, Factory, Clock, TestTube, FileCheck, Truck, Award as Certificate, HeartHandshake } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { companyInfo, qualityFeatures, partners } from '../mock';

export const About = () => {
  return (
    <section id="about" className="py-20 bg-slate-50">
      <div className="container mx-auto px-4">
        {/* Company Overview */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              {companyInfo.experience} of Excellence in Metal Supply
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                Since {companyInfo.established}, JPS Metals & Alloys has been India's trusted partner for premium 
                stainless steel and high-performance alloys. What started as a vision to serve India's growing 
                industrial needs has evolved into a legacy of quality, reliability, and technical expertise.
              </p>
              <p>
                We've built strong partnerships with leading manufacturers including Viraj Profiles, Ambica Steels, 
                and Laxcon Steels, ensuring our customers receive only certified, traceable materials that meet 
                the most stringent international standards.
              </p>
              <p>
                Our {companyInfo.certifications[0]} certification and commitment to continuous improvement drive 
                us to exceed customer expectations through transparency, innovation, and unwavering focus on quality.
              </p>
            </div>
            
            <div className="flex items-center space-x-8 mt-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-[#022d47]">40+</div>
                <div className="text-sm text-slate-600">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#022d47]">5000+</div>
                <div className="text-sm text-slate-600">Products</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#022d47]">500+</div>
                <div className="text-sm text-slate-600">Satisfied Clients</div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="bg-white p-8 rounded-2xl shadow-xl">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Our Mission</h3>
              <p className="text-slate-600 mb-6">
                To be India's most trusted supplier of premium metals and alloys, delivering 
                exceptional quality, technical expertise, and customer service that enables 
                our clients' success across diverse industrial applications.
              </p>
              
              <div className="space-y-3">
                {['Quality First Approach', 'Customer-Centric Service', 'Technical Excellence', 'Sustainable Practices'].map((value, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-slate-700 font-medium">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Quality Features */}
        <div className="mb-20">
          <h3 className="text-3xl font-bold text-slate-900 text-center mb-12">
            Why Choose JPS Metal & Alloys
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {qualityFeatures.map((feature, index) => {
              const iconMap = {
                'Certificate': Certificate,
                'TestTube': TestTube,
                'FileCheck': FileCheck,
                'Shield': Shield,
                'HeartHandshake': HeartHandshake
              };
              const IconComponent = iconMap[feature.icon] || Award;
              
              return (
                <Card key={index} className="border-0 shadow-lg bg-white">
                  <CardHeader className="pb-3">
                    <div className="w-12 h-12 bg-[#022d47]/10 rounded-lg flex items-center justify-center mb-3">
                      <IconComponent className="h-6 w-6 text-[#022d47]" />
                    </div>
                    <CardTitle className="text-lg text-slate-900">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-slate-600">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Partners Section */}
        <div className="bg-white rounded-2xl p-12">
          <h3 className="text-3xl font-bold text-slate-900 text-center mb-8">
            Trusted Manufacturing Partners
          </h3>
          <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            We collaborate with industry-leading manufacturers to ensure consistent quality, 
            reliable supply, and comprehensive material traceability.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {partners.map((partner, index) => (
              <div key={index} className="text-center p-6 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                <Factory className="h-8 w-8 text-[#022d47] mx-auto mb-3" />
                <div className="font-medium text-slate-800">{partner}</div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Button className="bg-[#022d47] hover:bg-[#033858] text-white px-8">
              View All Certifications
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};